import { useFormikContext } from 'formik'
import unique from 'lodash/fp/uniq'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'

import config from '@config'
import { CheckoutFormFields } from '@enums'
import { useTranslation } from '@lib/i18n'
import { useDiscountCardsLoader } from '@loaders/discountCards'
import ContactDetails from '@pages/Checkout/ContactDetails'
import Discounts from '@pages/Checkout/Discounts'
import Extras from '@pages/Checkout/Extras'
import FareClassesSelection from '@pages/Checkout/FareClassesSelection'
import SplitFareClasses from '@pages/Checkout/FareClassesSelection/SplitFareClasses'
import useFareClass from '@pages/Checkout/hooks/useFareClass'
import { CheckoutFormData } from '@pages/Checkout/hooks/useInitialFormValues'
import { useShowExtras } from '@pages/Checkout/hooks/useShowExtras'
import PassengerDetails from '@pages/Checkout/PassengerDetails'
import SeatSelection from '@pages/Checkout/SeatSelection'
import { FieldsetProvider } from '@pages/context/fieldset'
import PluginSlot from '@plugins/Slot'
import useMediaQueries from '@queries/media'
import { useSettings } from '@queries/settings'
import { useCheckout } from '@stores/checkout'
import { useParams } from '@stores/params'

interface Props {
  disabled: boolean
  supportedFields?: CheckoutFormFields[]
}

const DEFAULT_FIELDS_AND_ORDER: CheckoutFormFields[] = [
  CheckoutFormFields.SplitFareClasses,
  CheckoutFormFields.PassengerDetails,
  CheckoutFormFields.Contact,
  CheckoutFormFields.FareClassSelection,
  CheckoutFormFields.Discounts,
  CheckoutFormFields.Seats,
  CheckoutFormFields.Extras,
]

const ExtrasFallback = ({
  outbound,
  inbound,
  preselectedAncillaries,
}: {
  outbound: Connection | null
  inbound: Connection | null
  preselectedAncillaries?: string[]
}) => (
  <div className="cell">
    <div className="checkout__section">
      <Extras outbound={outbound} inbound={inbound} preselectedAncillaries={preselectedAncillaries} />
    </div>
  </div>
)

const EmptyFieldsFallback = () => {
  const { t } = useTranslation()

  return (
    <div className="cell">
      <div className="checkout__section">{t('checkout.emptyFields')}</div>
    </div>
  )
}

const CheckoutFields = ({ disabled, supportedFields = DEFAULT_FIELDS_AND_ORDER }: Props): ReactElement => {
  const [{ outbound, inbound }] = useCheckout()
  const [{ ancillaries, discountCodes, media, email, phone, passengerDetails, checkout }] = useSettings()
  const { mandatory } = passengerDetails
  const [{ currency, locale, retailerPartnerNumber, marketingCarrierCode }] = useParams()
  const { values, setFieldValue } = useFormikContext<CheckoutFormData>()
  const [seatsError, setSeatsError] = useState<ErrorCode | null>(null)
  const { showFareClassSelection, showFareClassSplit, fareClassList } = useFareClass()
  const isAvoidVacancy = config.amendments.avoidVacancy.byRpn.includes(retailerPartnerNumber)

  const isDiscountsEnabled =
    discountCodes.enabled && (discountCodes.displayOn === 'checkout' || discountCodes.displayOn === 'everywhere')
  const isMediaEnabled = media.enabled && (media.displayOn === 'checkout' || media.displayOn === 'everywhere')

  const { isLoading, data: cards } = useDiscountCardsLoader(
    {
      locale,
      retailerPartnerNumber,
      types: ['discount_card', 'loyalty'],
      ...(marketingCarrierCode && { marketingCarrierCodes: [marketingCarrierCode] }),
    },
    { enabled: isDiscountsEnabled },
  )

  const [mediaData] = useMediaQueries({
    connections: [outbound, inbound].filter(Boolean) as Connection[],
    enabled: isMediaEnabled,
  })

  const { showExtras, showSeats } = useShowExtras({ values, outbound, inbound })
  const bookingCards = useMemo(() => cards?.filter(c => c.context === 'booking') || [], [cards])
  const showDiscounts = isLoading || (isDiscountsEnabled && bookingCards && bookingCards.length > 0)
  const showContact = email.enabled || phone.enabled
  const isSeatsLoading = !outbound || (!isAvoidVacancy && values.isVacancyLoading)
  const fieldsOrder = useMemo(
    () => unique([...(checkout.widgetOrder || []), ...DEFAULT_FIELDS_AND_ORDER]),
    [checkout.widgetOrder],
  )
  const availableFields = useMemo(
    () => fieldsOrder.filter(field => supportedFields.includes(field)),
    [supportedFields, fieldsOrder],
  )

  useEffect(() => {
    setFieldValue('vacancies', [])
  }, [currency, setFieldValue])

  const renderedFields = availableFields
    .map(field => {
      if (field === CheckoutFormFields.SplitFareClasses && showFareClassSplit) {
        return (
          <div key={CheckoutFormFields.SplitFareClasses} className="cell">
            <SplitFareClasses media={mediaData} />
          </div>
        )
      }

      if (field === CheckoutFormFields.PassengerDetails) {
        return (
          <div key={CheckoutFormFields.PassengerDetails} className="cell">
            <div className="checkout__section">
              <PassengerDetails key={CheckoutFormFields.PassengerDetails} />
            </div>
          </div>
        )
      }

      if (field === CheckoutFormFields.Contact && showContact) {
        return (
          <div key="contact" className="cell">
            <div className="checkout__contact-details">
              <ContactDetails key="contact" />
            </div>
          </div>
        )
      }

      if (field === CheckoutFormFields.FareClassSelection && showFareClassSelection) {
        return (
          <div key={CheckoutFormFields.FareClassSelection} className="cell">
            <div className="checkout__section">
              <FareClassesSelection fareClassList={fareClassList} isLoading={!outbound} media={mediaData} />
            </div>
          </div>
        )
      }

      if (field === CheckoutFormFields.Discounts && showDiscounts) {
        return (
          <div key={CheckoutFormFields.Discounts} className="cell">
            <div className="checkout__section">
              <PluginSlot
                path="checkout.discounts"
                FallbackComponent={Discounts}
                props={{ discountCards: bookingCards, isLoading: isLoading || values.isVacancyLoading }}
              />
            </div>
          </div>
        )
      }

      if (field === CheckoutFormFields.Seats && showSeats && !seatsError) {
        return (
          <div key={CheckoutFormFields.Seats} className="cell">
            <div className="checkout__section">
              <SeatSelection onSeatsError={setSeatsError} isLoading={isSeatsLoading} />
              {showExtras && (
                <PluginSlot
                  path="checkout.extrasGeb"
                  props={{ outbound, inbound, preselectedAncillaries: ancillaries.preselected }}
                />
              )}
            </div>
          </div>
        )
      }

      if (field === CheckoutFormFields.Extras && showExtras) {
        return (
          <PluginSlot
            key={CheckoutFormFields.Extras}
            props={{ outbound, inbound, preselectedAncillaries: ancillaries.preselected }}
            FallbackComponent={ExtrasFallback}
            path="checkout.extras"
          />
        )
      }
    })
    .filter(Boolean)

  return (
    <FieldsetProvider
      className="checkout__fieldset column gap-4 p-0"
      value={{ isDisabled: disabled, isMandatory: mandatory }}
    >
      {renderedFields.length > 0 ? renderedFields : <EmptyFieldsFallback />}
    </FieldsetProvider>
  )
}

export default CheckoutFields
